import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import api from 'api';
import { Enterprise } from 'models/Enterprises';
import {
  EnterpriseReportGenerateClickedEventProps,
  EnterpriseReportPopupOpenedEventProps,
  ErrorViewedProps,
} from 'models/MixpanelEvents';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import mixpanelService from 'services/mixpanelService';
import downloadService from 'services/downloadService';
import { getMixpanelErrorProps } from 'axiosUtils';
import { FormValues, USAGE_REPORT_PERIOD_LABEL_KEYS } from './data';
import ExportUsageReportForm from './components/ExportUsageReportForm';
import DownloadIcon from 'icons/Download.icon';
import { getUsageReportDateFormat, getUsageReportRange } from './utils';

import './ExportUsageReportModal.styles.scss';

type ExportUsageReportModalProps = {
  className?: string;
  enterprise: Partial<Enterprise>;
};

const ExportUsageReportModal: React.FC<ExportUsageReportModalProps> = (
  props,
) => {
  const { className, enterprise } = props;

  const {
    _id: enterpriseId,
    brandName,
    numberOfBusinessUnits,
    numberOfEmployees,
    createdAt,
  } = enterprise;

  const earliestRecordDate = useMemo(
    () => (createdAt ? new Date(createdAt) : undefined),
    [createdAt],
  );

  const { t } = useTranslation();

  const { currentUser, enterpriseMember, updatePreferences } =
    useContext(CurrentUserContext);
  const { email, seenEnterpriseUsageReportFeature } = currentUser || {};
  const { seniority } = enterpriseMember || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const {
    startEnterpriseUsageReportExportDownload,
    failEnterpriseUsageReportExportDownload,
  } = useContext(DownloadStatusContext);

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('export-usage-report-modal', className);

  const mixpanelProps = useMemo(
    () => ({ email, seniority, enterpriseId, enterpriseName: brandName }),
    [brandName, email, enterpriseId, seniority],
  );

  const onClose = useCallback(() => modal?.close(), [modal]);

  const onOpen = useCallback(() => {
    modal?.open();

    if (!seenEnterpriseUsageReportFeature)
      updatePreferences({ seenEnterpriseUsageReportFeature: true });

    mixpanelService.enterpriseReportPopupOpened(
      getMixpanelProps({
        ...mixpanelProps,
        enterpriseBusinessUnitCount: numberOfBusinessUnits,
        enterpriseMemberCount: numberOfEmployees,
      }) as EnterpriseReportPopupOpenedEventProps,
    );
  }, [
    modal,
    seenEnterpriseUsageReportFeature,
    updatePreferences,
    mixpanelProps,
    numberOfBusinessUnits,
    numberOfEmployees,
    getMixpanelProps,
  ]);

  const onSubmit = useCallback(
    async ({ period }: FormValues) => {
      const { range } = getUsageReportRange(
        period,
        new Date(),
        earliestRecordDate,
      );

      mixpanelService.enterpriseReportGenerateClicked(
        getMixpanelProps({
          ...mixpanelProps,
          periodKey: t(USAGE_REPORT_PERIOD_LABEL_KEYS[period]),
          periodStart: getUsageReportDateFormat(range.start),
          periodEnd: getUsageReportDateFormat(range.end),
        }) as EnterpriseReportGenerateClickedEventProps,
      );

      try {
        startEnterpriseUsageReportExportDownload();

        await api.enterpriseAnalytics.exportUsageReport(
          enterpriseId,
          period,
          downloadService.downloadUniqueId,
        );

        onClose();
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
        failEnterpriseUsageReportExportDownload();
      }
    },
    [
      earliestRecordDate,
      mixpanelProps,
      enterpriseId,
      onClose,
      getMixpanelProps,
      startEnterpriseUsageReportExportDownload,
      failEnterpriseUsageReportExportDownload,
      t,
    ],
  );

  return (
    <>
      <Button
        variant="outline"
        icon={DownloadIcon}
        className={classNames('export-usage-report-modal__trigger', {
          'new-feature': !seenEnterpriseUsageReportFeature,
        })}
        onClick={onOpen}
      >
        {t('exportReport')}
      </Button>

      <Modal
        ref={modalRef}
        className={classes}
        title={
          <div className="export-usage-report-modal__title">
            <h4>{t('exportEnterpriseUsageReport')}</h4>
            <p className="export-usage-report-modal__subtitle">{brandName}</p>
          </div>
        }
      >
        <ExportUsageReportForm
          earliestRecordDate={earliestRecordDate}
          onSubmit={onSubmit}
          onClose={onClose}
        />
      </Modal>
    </>
  );
};

export default ExportUsageReportModal;
